/**
 * Gesammelte Orden: immer im Browser, mit Konto zusätzlich im Profil (Spalte `medals`, Migration 0007).
 * Fehlt die Spalte noch, bleibt es still beim Browser-Speicher.
 */
import { supabase } from '../../lib/supabase';
import { medalsFromCareer } from './medals';
import type { GameState } from '../../engine/types';

export type Earned = Record<string, { at: string; party: string; diff: string }>;
const KEY = 'kabinett-orden';
const listeners = new Set<() => void>();

export function loadLocal(): Earned { try { return JSON.parse(localStorage.getItem(KEY) || '{}') || {}; } catch { return {}; } }
function saveLocal(e: Earned) { try { localStorage.setItem(KEY, JSON.stringify(e)); } catch { /* privater Modus */ } listeners.forEach((f) => f()); }
export function onMedals(f: () => void) { listeners.add(f); return () => { listeners.delete(f); }; }

async function uid(): Promise<string | null> {
  if (!supabase) return null;
  const { data } = await supabase.auth.getUser(); return data.user?.id ?? null;
}
async function pushCloud(e: Earned) {
  const id = await uid(); if (!id || !supabase) return;
  await supabase.from('profiles').update({ medals: e }).eq('id', id); // Fehler (z. B. Spalte fehlt) bewusst ignoriert
}

/** Neue Orden eintragen. Gibt die zurück, die wirklich neu sind. */
export function earn(ids: string[], ctx: { party: string; diff: string; at?: string }): string[] {
  const e = loadLocal(), fresh = ids.filter((id) => !e[id]);
  if (!fresh.length) return [];
  const at = ctx.at || new Date().toISOString();
  for (const id of fresh) e[id] = { at, party: ctx.party, diff: ctx.diff };
  saveLocal(e); void pushCloud(e);
  return fresh;
}

/** Höchste gespielte Erfahrungsstufe (Version 12). Liegt als Eintrag `_stufe` mit bei den Orden, damit sie mit Konto
 * ohne eigene Spalte im Profil landet. Die Anzeige der Orden kennt diesen Schlüssel nicht und überspringt ihn. */
export const LEVEL_KEY = '_stufe';
const lvOfE = (e: Earned) => { const m = /^stufe-(\d)$/.exec(e[LEVEL_KEY]?.diff || ''); return m ? +m[1] : 0; };
export const savedLevel = (): number => lvOfE(loadLocal());
export function saveLevel(l: number) {
  const e = loadLocal(); if (lvOfE(e) >= l) return;
  e[LEVEL_KEY] = { at: new Date().toISOString(), party: '', diff: `stufe-${l}` };
  saveLocal(e); void pushCloud(e);
}

/** Nach dem Anmelden: Orden aus Browser und Konto zusammenführen. */
export async function syncMedals() {
  const id = await uid(); if (!id || !supabase) return;
  const { data, error } = await supabase.from('profiles').select('medals').eq('id', id).maybeSingle();
  if (error || !data) return;
  const cloud = ((data as any).medals || {}) as Earned, local = loadLocal();
  const merged: Earned = { ...cloud };
  for (const k in local) if (k !== LEVEL_KEY && (!merged[k] || local[k].at < merged[k].at)) merged[k] = local[k];
  const lvUp = lvOfE(local) > lvOfE(cloud); if (lvUp) merged[LEVEL_KEY] = local[LEVEL_KEY];
  saveLocal(merged);
  if (lvUp || Object.keys(merged).length !== Object.keys(cloud).length) void pushCloud(merged);
}

// ── Nachträglich vergeben ──

const RETRO_KEY = 'kabinett-orden-retro2'; // v2: robusteres Nachspielen, alle Spielstände noch einmal prüfen
type RetroDone = { seeds?: number[]; runs?: string[] };
function retroDone(): RetroDone { try { return JSON.parse(localStorage.getItem(RETRO_KEY) || '{}') || {}; } catch { return {}; } }
function setRetroDone(d: RetroDone) { try { localStorage.setItem(RETRO_KEY, JSON.stringify(d)); } catch { /* egal */ } }

/** Partien im Web Worker nachspielen. Gibt die Anzahl neu vergebener Orden zurück, -1 wenn es nicht geklappt hat. */
function runRetro(games: { key: string; seed: number; journal: unknown[]; at?: string }[]): Promise<number> {
  if (!games.length || typeof Worker === 'undefined') return Promise.resolve(0);
  return new Promise((ok) => {
    let w: Worker;
    try { w = new Worker(new URL('./retro.worker.ts', import.meta.url), { type: 'module' }); } catch { ok(-1); return; }
    const stop = setTimeout(() => { w.terminate(); ok(-1); }, 180000);
    w.onmessage = (e: MessageEvent<{ key: string; at?: string; hits: { id: string; party: string; diff: string }[] }[]>) => {
      clearTimeout(stop); w.terminate();
      let n = 0;
      for (const r of e.data) for (const h of r.hits) n += earn([h.id], { party: h.party, diff: h.diff, at: r.at }).length;
      ok(n);
    };
    w.onerror = () => { clearTimeout(stop); w.terminate(); ok(-1); };
    w.postMessage({ games });
  });
}

/** Die geladene Partie einmal nachspielen (je Partie, erkannt am Seed). Auch nach dem Laden aus der Cloud. */
export async function retroLocal(seed: number | undefined, journal: unknown[]): Promise<number> {
  const d = retroDone(); if (seed == null || !journal.length || (d.seeds || []).includes(seed)) return 0;
  const n = await runRetro([{ key: 'local', seed, journal: journal.slice() }]);
  if (n < 0) return 0; // beim nächsten Laden noch einmal versuchen
  const now = retroDone(); setRetroDone({ ...now, seeds: [...(now.seeds || []), seed].slice(-50) });
  return n;
}

export async function retroRuns(): Promise<number> {
  const id = await uid(); if (!id || !supabase) return 0;
  const done = new Set(retroDone().runs || []);
  const { data, error } = await supabase.from('runs').select('id,seed,journal,created_at').eq('user_id', id).not('journal', 'is', null).order('created_at', { ascending: false }).limit(50);
  if (error || !data) return 0;
  const games = (data as any[]).filter((r) => !done.has(r.id) && Array.isArray(r.journal)).map((r) => ({ key: r.id, seed: Number(r.seed), journal: r.journal, at: r.created_at }));
  const n = await runRetro(games);
  if (n < 0) return 0;
  setRetroDone({ ...retroDone(), runs: [...done, ...games.map((g) => g.key)] });
  return n;
}

/** Orden aus dem bisherigen Verlauf der geladenen Partie (Wiederwahl, Ära, Gesetzgeber …). Bei jedem Laden, kostet nichts. */
export function careerMedals(S: GameState | null | undefined): number {
  if (!S || S.mp) return 0;
  return earn(medalsFromCareer(S), { party: S.name, diff: (S.cfg as any)?.diff ?? 'normal' }).length;
}
